import React, { useCallback } from 'react'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientClients } from '../../../../store/crossClient/crossClientResults'
import { type ButtonVariant } from '../../../../components/Button/Button'
import { type CrossClientQueryResult } from '../../../../models/CrossClient'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { ActionsDropdownItem, ActionsToggle, CrossClientActionsDropdown } from '../../Actions/CrossClientActionsDropdown'
import { useCrmActions } from '../../Actions/useCrmActions'
import { useActionTracking } from '../../Actions/useActionTracking'
import { useZapierAction } from '../../Actions/useZapierAction'
import { ReactComponent as ZapierLogo } from '../../../../assets/zapier_logo.svg'
import { useExportCrossClientResults } from '../../Results/hooks'
import classNames from 'classnames'

export interface CrossClientResultButtonsProps {
  result: CrossClientQueryResult
  variant?: ButtonVariant
  withText?: boolean
  className?: string
}

export const CrossClientResultButtons: React.FC<CrossClientResultButtonsProps> = ({ result, variant = 'borderless', withText = false, className }) => {
  const clients = useAppSelector(selectCrossClientClients)

  const client = clients[result.clientId]

  const buttonClassName = classNames('p-2 cross-client-cta button-small border rounded-2', className)

  const crmActions = useCrmActions([result], 'single')
  const { trackActionClicked } = useActionTracking('single')
  const clientName = client ? getUserDisplayName(client) : undefined
  const { exportCSV } = useExportCrossClientResults(useCallback(() => Promise.resolve([result]), [result]))
  const { openZapier, zapierModal } = useZapierAction()

  const ariaLabel = clientName ? `Actions for ${clientName}` : 'Actions'

  return (
    <>
      <CrossClientActionsDropdown
        count={1}
        singleClientName={clientName}
        clientId={result.clientId}
        clientIntegrationServiceIds={client ? (client.integrations ?? []).map((integration) => integration.serviceId) : undefined}
        crmActions={crmActions}
        toggle={
          <ActionsToggle variant={variant} className={buttonClassName} aria-label={ariaLabel}>
            <i className='bi bi-three-dots fs-6' />
            {withText ? 'Actions' : null}
          </ActionsToggle>
        }
      >
        <ActionsDropdownItem icon='bi-envelope' label='Send email' to={`/email/new?contactId=${result.clientId}`} />
        <ActionsDropdownItem
          icon='bi-file-earmark-spreadsheet'
          label='Export as CSV'
          onClick={() => {
            trackActionClicked('Zocks', 'Export as CSV')
            exportCSV()
          }}
        />
        <ActionsDropdownItem iconNode={<ZapierLogo width={16} height={16} />} label='Send to Zapier' onClick={openZapier} />
      </CrossClientActionsDropdown>
      {zapierModal}
    </>
  )
}
